import { useCallback, useRef, useState, useEffect } from "react";

export const useResizePreview = ({ items, previewMode, totalColumns = 12 }) => {
  const [resizePreview, setResizePreview] = useState(null);
  const [isModifierPressed, setIsModifierPressed] = useState(false);
  const originalPositionsRef = useRef(new Map());

  // Listen for Shift key presses
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.shiftKey) {
        setIsModifierPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (!e.shiftKey) {
        setIsModifierPressed(false);
      }
    };

    // Handle when user switches windows/tabs while holding key
    const handleBlur = () => {
      setIsModifierPressed(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Simulate layout changes for resize preview using the same logic as actual resize
  const simulateResizeLayout = useCallback(
    (widgetId, newColumns, newColumnHeight, shouldDisplaceWidgets = false) => {
      const targetWidget = items.find((item) => item.id === widgetId);
      if (!targetWidget) return null;

      const currentLayout = targetWidget.layout[previewMode];
      if (!currentLayout) return null;

      // Store original positions if not already stored
      if (originalPositionsRef.current.size === 0) {
        items.forEach((item) => {
          if (item.layout[previewMode]) {
            originalPositionsRef.current.set(item.id, {
              position: item.layout[previewMode].position,
              startColumn: item.layout[previewMode].startColumn,
              columns: item.layout[previewMode].columns,
              columnHeight: item.layout[previewMode].columnHeight,
            });
          }
        });
      }

      // Use the same logic as actual resize in EditableWidget
      const currentStartCol = currentLayout.startColumn || 0;
      const currentPosition = currentLayout.position || 0;

      // Calculate new startColumn, adjusting if resizing would push widget off the right edge
      let newStartColumn = currentStartCol;
      const maxColumns = totalColumns || 12;

      // If widget would exceed right edge after resize, adjust startColumn
      if (currentStartCol + newColumns > maxColumns) {
        newStartColumn = Math.max(0, maxColumns - newColumns);
      }

      // Create a working copy of all widgets
      const workingWidgets = items.map((w) => ({
        ...w,
        layout: JSON.parse(JSON.stringify(w.layout)), // Deep clone layout
      }));

      // Update the resizing widget in the working copy
      const resizingWidget = workingWidgets.find((w) => w.id === widgetId);
      if (!resizingWidget) return null;

      // Apply the updated layout to the resizing widget
      resizingWidget.layout[previewMode] = {
        ...resizingWidget.layout[previewMode],
        columns: newColumns,
        columnHeight: newColumnHeight,
        startColumn: newStartColumn,
        position: currentPosition,
      };

      // Build widget map for collision detection
      const widgetMap = new Map();
      const lockedWidgets = [];

      workingWidgets.forEach((item) => {
        const itemLayout = item.layout[previewMode];
        const widgetInfo = {
          id: item.id,
          position: itemLayout?.position || 0,
          height: itemLayout?.columnHeight || 4,
          startColumn: itemLayout?.startColumn || 0,
          columns: itemLayout?.columns || 4,
          locked: item.layout?.settings?.locked || false,
          item: item,
        };

        widgetMap.set(item.id, widgetInfo);

        if (widgetInfo.locked) {
          lockedWidgets.push(widgetInfo);
        }
      });

      // Get the resizing widget from map
      const resizingInfo = widgetMap.get(widgetId);
      let adjustedPosition = resizingInfo.position;

      // Check for locked widget conflicts
      for (const lockedWidget of lockedWidgets) {
        const hasVerticalOverlap = !(
          adjustedPosition + newColumnHeight <= lockedWidget.position ||
          adjustedPosition >= lockedWidget.position + lockedWidget.height
        );

        const hasHorizontalOverlap = !(
          newStartColumn + newColumns <= lockedWidget.startColumn ||
          newStartColumn >= lockedWidget.startColumn + lockedWidget.columns
        );

        if (hasVerticalOverlap && hasHorizontalOverlap) {
          adjustedPosition = Math.max(
            adjustedPosition,
            lockedWidget.position + lockedWidget.height
          );
        }
      }

      // Update resizing widget position if adjusted
      if (adjustedPosition !== resizingInfo.position) {
        resizingInfo.position = adjustedPosition;
        resizingWidget.layout[previewMode].position = adjustedPosition;
      }

      // Track all position changes
      const positionChanges = new Map();

      // Add the resizing widget's changes
      positionChanges.set(widgetId, {
        position: adjustedPosition,
        startColumn: newStartColumn,
        columns: newColumns,
        columnHeight: newColumnHeight,
      });

      // Only process layout conflicts if displacement is enabled
      const processQueue = [];

      if (shouldDisplaceWidgets) {
        // Find initial conflicts
        widgetMap.forEach((widget) => {
          if (widget.locked || widget.id === widgetId) return;

          const hasVerticalOverlap = !(
            adjustedPosition + newColumnHeight <= widget.position ||
            adjustedPosition >= widget.position + widget.height
          );

          const hasHorizontalOverlap = !(
            newStartColumn + newColumns <= widget.startColumn ||
            newStartColumn >= widget.startColumn + widget.columns
          );

          if (hasVerticalOverlap && hasHorizontalOverlap) {
            const pushAmount =
              adjustedPosition + newColumnHeight - widget.position;
            if (pushAmount > 0) {
              processQueue.push({
                widget: widget,
                newPosition: widget.position + pushAmount,
              });
            }
          }
        });
      }

      // Process the queue (only if displacement is enabled)
      const movedWidgets = new Map();

      while (processQueue.length > 0 && shouldDisplaceWidgets) {
        const { widget, newPosition } = processQueue.shift();

        if (
          movedWidgets.has(widget.id) &&
          movedWidgets.get(widget.id) >= newPosition
        ) {
          continue;
        }

        let finalPosition = newPosition;

        // Check locked widget conflicts
        for (const lockedWidget of lockedWidgets) {
          const wouldOverlapVertically = !(
            finalPosition + widget.height <= lockedWidget.position ||
            finalPosition >= lockedWidget.position + lockedWidget.height
          );

          const wouldOverlapHorizontally = !(
            widget.startColumn + widget.columns <= lockedWidget.startColumn ||
            widget.startColumn >=
              lockedWidget.startColumn + lockedWidget.columns
          );

          if (wouldOverlapVertically && wouldOverlapHorizontally) {
            finalPosition = Math.max(
              finalPosition,
              lockedWidget.position + lockedWidget.height
            );
          }
        }

        // Check conflicts with previously moved widgets
        movedWidgets.forEach((movedPosition, movedWidgetId) => {
          const movedWidgetInfo = widgetMap.get(movedWidgetId);
          if (!movedWidgetInfo) return;

          const wouldOverlapVertically = !(
            finalPosition + widget.height <= movedPosition ||
            finalPosition >= movedPosition + movedWidgetInfo.height
          );

          const wouldOverlapHorizontally = !(
            widget.startColumn + widget.columns <=
              movedWidgetInfo.startColumn ||
            widget.startColumn >=
              movedWidgetInfo.startColumn + movedWidgetInfo.columns
          );

          if (wouldOverlapVertically && wouldOverlapHorizontally) {
            finalPosition = Math.max(
              finalPosition,
              movedPosition + movedWidgetInfo.height
            );
          }
        });

        movedWidgets.set(widget.id, finalPosition);

        // Update the widget position in the map immediately
        widget.position = finalPosition;
        widgetMap.set(widget.id, widget);

        // Track this change - only add to displaced if it's not the resized widget
        if (widget.id !== widgetId) {
          positionChanges.set(widget.id, {
            position: finalPosition,
            startColumn: widget.startColumn,
            columns: widget.columns,
            columnHeight: widget.height,
          });
        }

        // Check for new layout conflicts using updated positions
        widgetMap.forEach((otherWidget) => {
          if (
            otherWidget.id === widget.id ||
            otherWidget.locked ||
            movedWidgets.has(otherWidget.id) ||
            otherWidget.id === widgetId
          ) {
            return;
          }

          // Use the current position (may have been updated by previous moves)
          const currentOtherPosition = movedWidgets.has(otherWidget.id)
            ? movedWidgets.get(otherWidget.id)
            : otherWidget.position;

          const hasVerticalOverlap = !(
            finalPosition + widget.height <= currentOtherPosition ||
            finalPosition >= currentOtherPosition + otherWidget.height
          );

          const hasHorizontalOverlap = !(
            widget.startColumn + widget.columns <= otherWidget.startColumn ||
            widget.startColumn >= otherWidget.startColumn + otherWidget.columns
          );

          if (hasVerticalOverlap && hasHorizontalOverlap) {
            const otherPushAmount =
              finalPosition + widget.height - currentOtherPosition;
            if (otherPushAmount > 0) {
              const newOtherPosition = currentOtherPosition + otherPushAmount;

              // Only add to queue if not already processed with equal or greater position
              const existingQueueItem = processQueue.find(
                (q) => q.widget.id === otherWidget.id
              );
              if (existingQueueItem) {
                // Update existing queue item if new position is greater
                if (newOtherPosition > existingQueueItem.newPosition) {
                  existingQueueItem.newPosition = newOtherPosition;
                }
              } else if (
                !movedWidgets.has(otherWidget.id) ||
                movedWidgets.get(otherWidget.id) < newOtherPosition
              ) {
                processQueue.push({
                  widget: otherWidget,
                  newPosition: newOtherPosition,
                });
              }
            }
          }
        });
      }

      // Convert positionChanges to displaced map (excluding the resized widget)
      const displaced = new Map();
      positionChanges.forEach((changes, id) => {
        if (id !== widgetId) {
          displaced.set(id, changes);
        }
      });

      return {
        displaced,
        resizeInfo: {
          id: widgetId,
          startColumn: newStartColumn,
          position: adjustedPosition,
          columns: newColumns,
          columnHeight: newColumnHeight,
          locked: targetWidget.layout?.settings?.locked || false,
        },
      };
    },
    [items, previewMode, totalColumns]
  );

  // Update preview when modifier key state changes
  useEffect(() => {
    if (resizePreview) {
      const previewResult = simulateResizeLayout(
        resizePreview.widgetId,
        resizePreview.newColumns,
        resizePreview.newColumnHeight,
        isModifierPressed
      );
      if (previewResult) {
        setResizePreview((prev) =>
          prev
            ? {
                ...prev,
                displaced: previewResult.displaced,
                resizeInfo: previewResult.resizeInfo,
              }
            : null
        );
      }
    }
  }, [isModifierPressed, simulateResizeLayout]);

  // Start a resize preview
  const startResizePreview = useCallback(
    (widgetId, newColumns, newColumnHeight) => {
      const previewResult = simulateResizeLayout(
        widgetId,
        newColumns,
        newColumnHeight,
        isModifierPressed
      );
      if (previewResult) {
        setResizePreview({
          widgetId,
          newColumns,
          newColumnHeight,
          displaced: previewResult.displaced,
          resizeInfo: previewResult.resizeInfo,
        });
      }
    },
    [simulateResizeLayout, isModifierPressed]
  );

  // Update resize preview with new dimensions
  const updateResizePreview = useCallback(
    (widgetId, newColumns, newColumnHeight) => {
      const previewResult = simulateResizeLayout(
        widgetId,
        newColumns,
        newColumnHeight,
        isModifierPressed
      );
      if (previewResult) {
        setResizePreview({
          widgetId,
          newColumns,
          newColumnHeight,
          displaced: previewResult.displaced,
          resizeInfo: previewResult.resizeInfo,
        });
      }
    },
    [simulateResizeLayout, isModifierPressed]
  );

  // Stop the resize preview and restore original positions
  const stopResizePreview = useCallback(() => {
    setResizePreview(null);
    originalPositionsRef.current.clear();
  }, []);

  // Get the displaced position for a widget during preview
  const getDisplacedPosition = useCallback(
    (widgetId) => {
      if (!resizePreview || !resizePreview.displaced.has(widgetId)) {
        return null;
      }
      return resizePreview.displaced.get(widgetId);
    },
    [resizePreview]
  );

  // Get the resize preview info for the currently resizing widget
  const getResizePreviewInfo = useCallback(() => {
    if (!resizePreview) return null;
    return {
      widgetId: resizePreview.widgetId,
      newColumns: resizePreview.newColumns,
      newColumnHeight: resizePreview.newColumnHeight,
      resizeInfo: resizePreview.resizeInfo,
      displaced: resizePreview.displaced,
    };
  }, [resizePreview]);

  // Check if a widget is currently being resized in preview
  const isWidgetBeingResized = useCallback(
    (widgetId) => {
      return resizePreview && resizePreview.widgetId === widgetId;
    },
    [resizePreview]
  );

  // Check if resize preview is active
  const isResizePreviewActive = useCallback(() => {
    return resizePreview !== null;
  }, [resizePreview]);

  return {
    resizePreview,
    startResizePreview,
    updateResizePreview,
    stopResizePreview,
    getDisplacedPosition,
    getResizePreviewInfo,
    isWidgetBeingResized,
    isResizePreviewActive,
    simulateResizeLayout,
    isModifierPressed,
  };
};
